import { GraphQLResult } from "@aws-amplify/api-graphql";
import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import { config } from "src/config";
import { DeepPartial } from "src/types/utils";

import { Mutation, Query, TypedDocumentString } from "../generated/graphql";
import { extractGraphqlOperationName, GraphQLRequestError, NormalizedGraphQLError } from "./utils";

type GraphQLOptions = {
    authMode?: "AMAZON_COGNITO_USER_POOLS" | "API_KEY" | "AWS_IAM";
};

export const graphqlFetch = async <
    TResult extends DeepPartial<Query> | DeepPartial<Mutation>,
    TVariables extends Record<string, unknown>,
>(
    query: TypedDocumentString<TResult, TVariables>,
    variables?: TVariables extends Record<string, unknown> ? TVariables : never,
    options?: GraphQLOptions
): Promise<NonNullable<GraphQLResult<TResult>["data"]>> => {
    const queryString = query.toString();
    const { name: operationName, type: operationType } = extractGraphqlOperationName(queryString) || {};

    Sentry.addBreadcrumb({
        category: "graphql",
        level: "info",
        message: `GraphQL ${operationName || "operation"} start`,
        data: {
            operationName,
            variablesKeys: Object.keys((variables as Record<string, unknown>) || {}).sort(),
        },
    });

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (!options?.authMode || options?.authMode === "AMAZON_COGNITO_USER_POOLS") {
        const session = await Auth.currentSession();
        const token = session?.getIdToken()?.getJwtToken();
        headers.Authorization = `Bearer ${token}`;
    } else if (options?.authMode === "API_KEY") {
        headers["x-api-key"] = config.api.key;
    }

    const response = await fetch(config.api.endpointUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: queryString, variables }),
    });

    const requestId = response.headers.get("x-amzn-requestid");

    if (!response.ok) {
        throw new GraphQLRequestError({
            message: `GraphQL request failed: ${response.status} ${response.statusText}`,
            operationType,
            operationName,
            normalized: [],
            variables,
            requestId,
        });
    }

    const responseData = (await response.json()) as GraphQLResult<TResult>;

    if (responseData.errors?.length) {
        const normalized: NormalizedGraphQLError[] = responseData.errors.map((e) => ({
            message: e?.message || "Unknown GraphQL error",
            path: Array.isArray(e?.path) ? e.path.join(".") : e?.path?.toString(),
            errorType: e?.extensions?.errorType || e?.extensions?.code,
            code: e?.extensions?.code,
            source: e?.source?.body,
        }));

        const message = normalized
            .map((e) => e.message)
            .filter(Boolean)
            .join("; ");
        const gqlError = new GraphQLRequestError<TVariables>({
            message,
            operationType,
            operationName,
            normalized,
            variables: variables as TVariables,
            requestId,
        });

        Sentry.addBreadcrumb({
            category: "graphql",
            level: "error",
            message: `GraphQL ${operationName || "operation"} failed`,
            data: {
                operationName,
                errorTypes: Array.from(new Set(normalized.map((n) => n.errorType)))
                    .filter(Boolean)
                    .join(", "),
                paths: Array.from(new Set(normalized.map((n) => n.path)))
                    .filter(Boolean)
                    .join(", "),
                requestId,
            },
        });
        throw gqlError;
    }

    if (!responseData.data) {
        const noDataError = new GraphQLRequestError<TVariables>({
            message: "GraphQL: no data returned",
            operationType,
            operationName,
            normalized: [],
            variables: variables as TVariables,
            requestId,
        });
        throw noDataError;
    }

    Sentry.addBreadcrumb({
        category: "graphql",
        level: "info",
        message: `GraphQL ${operationName || "operation"} success`,
        data: { operationName, requestId },
    });
    return responseData.data;
};
